import { useEffect, useRef, useState } from 'react';
import { Box, CssBaseline } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useAuth } from '../shared/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppTheme from '../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  // datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';
import BreadCrumbsWithIcons from '../shared/components/BreadCrumbsWithIcons';
import NotificationAlert from '../shared/components/NotificationAlert';
import SideMenu from '../shared/components/SideMenu';
import ErrorsDataGrid from './components/ErrorsDataGrid.jsx';
import ErrorViewDialog from './components/ErrorViewDialog.jsx';
import * as errorService from '../services/errorService';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  // ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function Errors(props) {
  const { auth, user, isAdmin, isAuthResolved } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [viewErrorRecord, setViewErrorRecord] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const pageCursorSnapshotsRef = useRef({ 0: null });
  const pendingPageResetRef = useRef(false);
  const latestFetchRequestRef = useRef(0);

  // Defense-in-depth: AdminRoute already gates this route on isAdmin, but
  // double-check here too in case this component is ever reached directly.
  useEffect(() => {
    if (!isAuthResolved) return;

    if (user) {
      setLoading(true);
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
      setLoading(false);
    }

    setAuthChecked(true);
  }, [isAuthResolved, user, isAdmin, navigate]);

  const activeUserIdFilterItem = filterModel.items.find((item) => item.field === 'userId' && item.value?.trim()) || null;
  const activePatientIdFilterItem = filterModel.items.find((item) => item.field === 'patientId' && item.value?.trim()) || null;
  const activeApiFilterItem = filterModel.items.find((item) => item.field === 'api' && item.value?.trim()) || null;

  const [debouncedFilters, setDebouncedFilters] = useState({
    userIdFilter: activeUserIdFilterItem?.value?.trim() || '',
    userIdFilterOperator: activeUserIdFilterItem?.operator || 'startsWith',
    patientIdFilter: activePatientIdFilterItem?.value?.trim() || '',
    patientIdFilterOperator: activePatientIdFilterItem?.operator || 'startsWith',
    apiFilter: activeApiFilterItem?.value?.trim() || '',
    apiFilterOperator: activeApiFilterItem?.operator || 'startsWith',
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters({
        userIdFilter: activeUserIdFilterItem?.value?.trim() || '',
        userIdFilterOperator: activeUserIdFilterItem?.operator || 'startsWith',
        patientIdFilter: activePatientIdFilterItem?.value?.trim() || '',
        patientIdFilterOperator: activePatientIdFilterItem?.operator || 'startsWith',
        apiFilter: activeApiFilterItem?.value?.trim() || '',
        apiFilterOperator: activeApiFilterItem?.operator || 'startsWith',
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [activeApiFilterItem, activePatientIdFilterItem, activeUserIdFilterItem]);

  const handleFilterModelChange = (nextFilterModel) => {
    const supportedFilterItem = (nextFilterModel?.items || []).find(
      (item) => item.field === 'userId' || item.field === 'patientId' || item.field === 'api'
    );

    const normalizedFilterItem = supportedFilterItem
      ? {
          ...supportedFilterItem,
          operator: supportedFilterItem.operator === 'equals' ? 'equals' : 'startsWith',
          value: supportedFilterItem.value || '',
        }
      : null;

    setFilterModel({
      ...nextFilterModel,
      items: normalizedFilterItem ? [normalizedFilterItem] : [],
    });
  };

  useEffect(() => {
    pageCursorSnapshotsRef.current = { 0: null };

    if (page !== 0) {
      pendingPageResetRef.current = true;
      setPage(0);
      return;
    }

    pendingPageResetRef.current = false;
  }, [
    pageSize,
    debouncedFilters.userIdFilter,
    debouncedFilters.userIdFilterOperator,
    debouncedFilters.patientIdFilter,
    debouncedFilters.patientIdFilterOperator,
    debouncedFilters.apiFilter,
    debouncedFilters.apiFilterOperator,
  ]);

  const resolvePageCursor = async (targetPage, targetPageSize) => {
    if (targetPage <= 0) return null;

    if (Object.prototype.hasOwnProperty.call(pageCursorSnapshotsRef.current, targetPage)) {
      return pageCursorSnapshotsRef.current[targetPage];
    }

    const knownPages = Object.keys(pageCursorSnapshotsRef.current)
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 0)
      .sort((left, right) => left - right);

    let currentPage = 0;
    let currentCursor = null;

    for (const knownPage of knownPages) {
      if (knownPage < targetPage) {
        currentPage = knownPage;
        currentCursor = pageCursorSnapshotsRef.current[knownPage];
      }
    }

    while (currentPage < targetPage) {
      const response = await errorService.listErrors({
        pageSize: targetPageSize,
        cursorSnapshot: currentCursor,
        ...debouncedFilters,
      });

      pageCursorSnapshotsRef.current[currentPage + 1] = response.lastVisibleSnapshot;
      currentCursor = response.lastVisibleSnapshot;
      currentPage += 1;

      if (!currentCursor) {
        break;
      }
    }

    return pageCursorSnapshotsRef.current[targetPage] ?? null;
  };

  const fetchPage = async () => {
    if (!authChecked || !user) return;

    const requestId = latestFetchRequestRef.current + 1;
    latestFetchRequestRef.current = requestId;
    setError(null);
    setLoading(true);

    try {
      const cursorSnapshot = await resolvePageCursor(page, pageSize);
      const data = await errorService.listErrors({
        pageSize,
        cursorSnapshot,
        ...debouncedFilters,
      });

      if (latestFetchRequestRef.current !== requestId) {
        return;
      }

      pageCursorSnapshotsRef.current[page + 1] = data.lastVisibleSnapshot;
      setHasMore(Boolean(data.hasMore));
      setRows((data.errors || []).map((errorRecord) => ({
        id: errorRecord.id,
        userId: errorRecord.userId || '',
        patientId: errorRecord.patientId || '',
        api: errorRecord.api || '',
        error: errorRecord.error || '',
      })));
    } catch (err) {
      if (latestFetchRequestRef.current !== requestId) {
        return;
      }

      console.error('listErrors failed', err);
      setError(err);
      setRows([]);
      setHasMore(false);
    } finally {
      if (latestFetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (pendingPageResetRef.current && page !== 0) {
      return;
    }

    pendingPageResetRef.current = false;
    fetchPage();
  }, [
    authChecked,
    page,
    pageSize,
    debouncedFilters.userIdFilter,
    debouncedFilters.userIdFilterOperator,
    debouncedFilters.patientIdFilter,
    debouncedFilters.patientIdFilterOperator,
    debouncedFilters.apiFilter,
    debouncedFilters.apiFilterOperator,
  ]);

  const handleRowView = (errorRecord) => {
    const id = errorRecord?.id;
    if (!id) return;

    setViewLoading(true);
    setError(null);

    errorService
      .getError(id)
      .then((full) => {
        setViewErrorRecord(full);
        setViewOpen(true);
      })
      .catch((err) => {
        console.error('getError for view failed', err);
        setError(err);
      })
      .finally(() => setViewLoading(false));
  };

  if (!authChecked) return null;

  return (
    <AppTheme themeComponents={xThemeComponents} {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <BreadCrumbsWithIcons
            items={[
              { key: 'home', label: 'Home', to: '/dashboard', Icon: HomeOutlinedIcon },
              { key: 'errors', label: 'Errors', to: null, Icon: ErrorOutlineOutlinedIcon },
            ]}
          />
          <NotificationAlert error={error} onClear={() => setError(null)} />
          <ErrorsDataGrid
            rows={rows}
            loading={loading}
            page={page}
            pageSize={pageSize}
            hasMore={hasMore}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            onPageChange={(nextPage) => setPage(nextPage)}
            onPageSizeChange={(nextPageSize) => setPageSize(nextPageSize)}
            onRowView={handleRowView}
          />
          <ErrorViewDialog
            open={viewOpen}
            errorRecord={viewErrorRecord}
            onClose={() => setViewOpen(false)}
            loading={viewLoading}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}